import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import type { QueryClientConfig } from '@tanstack/react-query';
import api from './apiClient';
import type { AdminProviderDirectoryQuery } from './apiClient';
import { GroupResponse, FacilityInGroupResponse } from '../types/api/group.types';
import { AdminUserListItem, AdminUserDetail } from '../types/api/admin.types';

export const defaultQueryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
};

// Auth Hooks
export const useGetAuthMe = (options?: UseQueryOptions<any>) => {
  return useQuery({ 
    queryKey: ['auth', 'me'], 
    queryFn: () => api.auth.me(),
    ...options
  });
};

// Group Hooks
export const useGetGroupById = (id?: string, options?: UseQueryOptions<any>) => {
  return useQuery({
    queryKey: ['group', id], 
    queryFn: () => id ? api.groups.getById(id) : null,
    enabled: !!id && (options?.enabled !== false),
    ...options
  });
};

export const usePatchGroupById = (options?: UseMutationOptions<GroupResponse, Error, { id: string; data: Partial<GroupResponse> }>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => api.groups.update(id, data),
    onSuccess: (data, variables, context) => {
      // Invalidate the appropriate queries
      queryClient.invalidateQueries({ queryKey: ['group', variables.id] });
    },
    ...options
  });
};

export const usePatchGroupReviewStatus = (
  options?: UseMutationOptions<any, Error, {
    id: string;
    underReview?: boolean;
    reviewStatus?: string;
    reviewNotes?: string;
    reviewReason?: string;
    // Mandatory server-side on reject and needs_info (D1.2.5).
    reasonCode?: string;
    needsInfoFields?: string[];
  }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) => api.groups.updateReviewStatus(id, payload),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'groups'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'group', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['group', variables.id] });
    },
    ...options,
  });
};

export const usePostGroupIntake = (options?: UseMutationOptions<any, Error, { data: any }>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }) => api.groups.submitIntake(data),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries on success
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
    ...options
  });
};

export const usePutGroupIntakeById = (options?: UseMutationOptions<any, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => api.groups.updateIntake(id, data),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries on success
      queryClient.invalidateQueries({ queryKey: ['group', variables.id] });
    },
    ...options
  });
};

// Facility Hooks
export const useGetGroupFacilitiesByGroupId = (
  groupId?: string,
  options?: UseQueryOptions<FacilityInGroupResponse[]>
) => {
  return useQuery({
    queryKey: ['facilities', 'group', groupId],
    queryFn: () => (groupId ? api.facilities.getByGroupId(groupId) : null),
    enabled: !!groupId && options?.enabled !== false,
    ...options,
  });
};

export const usePostGroupFacilitiesByGroupId = (options?: UseMutationOptions<any, Error, { groupId: string; data: any }>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }) => api.facilities.addToGroup(groupId, data),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries on success
      queryClient.invalidateQueries({ queryKey: ['facilities', 'group', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['group', variables.groupId] });
    },
    ...options
  });
};

export const usePatchFacilityById = (options?: UseMutationOptions<any, Error, { id: string; data: any }>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => api.facilities.update(id, data),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries on success
      queryClient.invalidateQueries({ queryKey: ['facilities'] });
    },
    ...options
  });
};

export const useDeleteGroupFacilitiesByGroupIdAndFacilityId = (
  options?: UseMutationOptions<any, Error, { groupId: string; facilityId: string }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, facilityId }) => api.facilities.removeFromGroup(groupId, facilityId),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries on success
      queryClient.invalidateQueries({ queryKey: ['facilities', 'group', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['group', variables.groupId] });
    },
    ...options
  });
};

// Listing Hooks
export const useGetFacilityListingsByFacilityId = (
  facilityId?: string,
  options?: UseQueryOptions<any>
) => {
  return useQuery({
    queryKey: ['listings', 'facility', facilityId],
    queryFn: () => facilityId ? api.listings.getByFacilityId(facilityId) : null,
    enabled: !!facilityId && (options?.enabled !== false),
    ...options
  });
};

export const usePostFacilityListingsByFacilityId = (
  options?: UseMutationOptions<any, Error, { facilityId: string; data: any }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ facilityId, data }) => api.listings.add(facilityId, data),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries on success
      queryClient.invalidateQueries({ queryKey: ['listings', 'facility', variables.facilityId] });
    },
    ...options
  });
};

export const usePatchFacilityListingByIdAndListingId = (
  options?: UseMutationOptions<any, Error, { id: string; listingId: string; data: any }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, listingId, data }) => api.listings.update(id, listingId, data),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries on success
      queryClient.invalidateQueries({ queryKey: ['listings', 'facility', variables.id] });
    },
    ...options
  });
};

export const useDeleteFacilityListingByIdAndListingId = (
  options?: UseMutationOptions<any, Error, { id: string; listingId: string }>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, listingId }) => api.listings.remove(id, listingId),
    onSuccess: (data, variables, context) => {
      // Invalidate relevant queries on success
      queryClient.invalidateQueries({ queryKey: ['listings', 'facility', variables.id] });
    },
    ...options
  });
};

// Admin user hooks
export const useGetAdminUsers = (
  role?: string,
  options?: UseQueryOptions<AdminUserListItem[]>
) => {
  return useQuery({
    queryKey: ['admin', 'users', role],
    queryFn: () => api.admin.users.list(role),
    ...options,
  });
};

export const useGetAdminUserById = (
  id?: string,
  options?: UseQueryOptions<AdminUserDetail>
) => {
  return useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => (id ? api.admin.users.getById(id) : null),
    enabled: !!id && options?.enabled !== false,
    ...options,
  });
};

// Admin groups hooks
export const useGetAdminGroups = (
  search?: string,
  page?: number,
  limit?: number,
  options?: UseQueryOptions<any>
) => {
  return useQuery({
    queryKey: ['admin', 'groups', search, page, limit],
    queryFn: () => api.admin.groups.list(search, page, limit),
    ...options,
  });
};

export const useGetAdminGroupById = (
  id?: string,
  options?: UseQueryOptions<any>
) => {
  return useQuery({
    queryKey: ['admin', 'group', id],
    queryFn: () => (id ? api.admin.groups.getById(id) : null),
    enabled: !!id && options?.enabled !== false,
    ...options,
  });
};

// Admin provider profiles hooks
export const useGetAdminProviderProfiles = (
  search?: string,
  page?: number,
  limit?: number,
  options?: UseQueryOptions<any>,
  directoryQuery?: AdminProviderDirectoryQuery
) => {
  return useQuery({
    queryKey: ['admin', 'providerProfiles', search, page, limit, directoryQuery],
    queryFn: () => api.admin.providerProfiles.list(search, page, limit, directoryQuery),
    ...options,
  });
};

export const useGetAdminProviderProfileById = (
  id?: string,
  options?: UseQueryOptions<any>
) => {
  return useQuery({
    queryKey: ['admin', 'providerProfile', id],
    queryFn: () => (id ? api.admin.providerProfiles.getById(id) : null),
    enabled: !!id && options?.enabled !== false,
    ...options,
  });
};
